import React, { useEffect, useRef, useState } from 'react';

/**
 * Lagani WYSIWYG editor (bez vanjskih ovisnosti) — contentEditable + toolbar preko document.execCommand.
 * Kontrolirana komponenta: parent drži HTML string (`value`), editor emitira `onChange(html)`.
 * Dovoljno za interne okružnice (bold/italic/podcrta, naslovi, liste, link). Cijena: execCommand je
 * deprecated ali univerzalno radi — izbjegavamo ~200kb editor lib.
 *
 * HTML-mode (</> toggle): prebaci na <textarea> sa sirovim HTML-om. Zalijepi li korisnik HTML IZVOR
 * (npr. gotovu čestitku) ovdje, sprema se kao pravi HTML i renderira na read/email/approval stranici.
 * (U WYSIWYG-u bi paste source koda postao escapean tekst → vidio bi se goli kod.)
 */
export function RichTextEditor({
    value, onChange, locale = 'hr', minHeight = 260, placeholder,
}: {
    value: string;
    onChange: (html: string) => void;
    locale?: 'hr' | 'en';
    minHeight?: number;
    placeholder?: string;
}) {
    const ref = useRef<HTMLDivElement | null>(null);
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [htmlMode, setHtmlMode] = useState(false);

    // Sinkroniziraj vanjski value → DOM samo kad se razlikuje i editor NIJE fokusiran (da ne skače kursor).
    // U htmlMode contentEditable nije montiran → effect preskače; pri povratku na WYSIWYG (htmlMode→false)
    // div se remounta pa se innerHTML postavi iz value (renderira HTML zalijepljen u textarei).
    useEffect(() => {
        const el = ref.current;
        if (!el || htmlMode) return;
        if (document.activeElement !== el && el.innerHTML !== (value || '')) {
            el.innerHTML = value || '';
        }
    }, [value, htmlMode]);

    const emit = () => { if (ref.current) onChange(ref.current.innerHTML); };
    const exec = (cmd: string, arg?: string) => {
        ref.current?.focus();
        try { document.execCommand(cmd, false, arg); } catch { /* */ }
        emit();
    };
    const link = () => {
        const url = window.prompt(t('URL poveznice:', 'Link URL:'), 'https://');
        if (url) exec('createLink', url);
    };

    const Btn = ({ cmd, arg, label, title }: { cmd: string; arg?: string; label: React.ReactNode; title: string }) => (
        <button type="button" title={title} className="okr-rte-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exec(cmd, arg)}>{label}</button>
    );
    const sep = <span className="okr-rte-sep" />;

    return (
        <div className="okr-rte">
            <div className="okr-rte-toolbar">
                {!htmlMode ? (
                    <>
                        <Btn cmd="bold" label={<strong>B</strong>} title={t('Podebljano', 'Bold')} />
                        <Btn cmd="italic" label={<em>I</em>} title={t('Kurziv', 'Italic')} />
                        <Btn cmd="underline" label={<u>U</u>} title={t('Podcrtano', 'Underline')} />
                        {sep}
                        <Btn cmd="formatBlock" arg="h2" label="H2" title={t('Naslov 2', 'Heading 2')} />
                        <Btn cmd="formatBlock" arg="h3" label="H3" title={t('Naslov 3', 'Heading 3')} />
                        <Btn cmd="formatBlock" arg="p" label="¶" title={t('Paragraf', 'Paragraph')} />
                        {sep}
                        <Btn cmd="insertUnorderedList" label="•" title={t('Lista', 'Bullet list')} />
                        <Btn cmd="insertOrderedList" label="1." title={t('Numerirana lista', 'Numbered list')} />
                        <Btn cmd="justifyLeft" label="⯇" title={t('Lijevo', 'Left')} />
                        <Btn cmd="justifyCenter" label="≡" title={t('Centrirano', 'Center')} />
                        {sep}
                        <button type="button" title={t('Poveznica', 'Link')} className="okr-rte-btn" onMouseDown={(e) => e.preventDefault()} onClick={link}>🔗</button>
                        <Btn cmd="unlink" label="⛓" title={t('Ukloni poveznicu', 'Unlink')} />
                        <Btn cmd="removeFormat" label="⌫" title={t('Očisti formatiranje', 'Clear formatting')} />
                        {sep}
                    </>
                ) : (
                    <span style={{ fontSize: '0.78rem', color: 'var(--ad-ink)', padding: '0 6px', alignSelf: 'center' }}>
                        {t('HTML izvor — zalijepi gotov HTML, pa klikni </> natrag za pregled.', 'HTML source — paste raw HTML, then click </> to preview.')}
                    </span>
                )}
                <button type="button" title={t('HTML izvor', 'HTML source')}
                    className={'okr-rte-btn' + (htmlMode ? ' is-active' : '')}
                    style={htmlMode ? { marginLeft: 'auto', background: 'var(--ad-ink)', color: '#fffbeb' } : { marginLeft: 'auto' }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setHtmlMode((m) => !m)}>&lt;/&gt; HTML</button>
            </div>
            {htmlMode ? (
                <textarea
                    className="okr-rte-surface"
                    value={value}
                    spellCheck={false}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={t('Zalijepi ili uredi HTML izvor…', 'Paste or edit HTML source…')}
                    style={{ minHeight, width: '100%', boxSizing: 'border-box', fontFamily: 'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace', fontSize: '0.82rem', lineHeight: 1.5, whiteSpace: 'pre', resize: 'vertical' }}
                />
            ) : (
                <div
                    ref={ref}
                    className="okr-rte-surface"
                    contentEditable
                    suppressContentEditableWarning
                    onInput={emit}
                    onBlur={emit}
                    data-placeholder={placeholder ?? ''}
                    style={{ minHeight }}
                />
            )}
        </div>
    );
}
